import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ROLE_CATEGORIES, KIT_ITEMS } from "@/pages/RegisterStaff";
import { UploadCloud, FileText, ImageIcon, Languages, ShieldCheck, PhoneCall } from "lucide-react";
import { STAFF } from "@/constants/testIds";
import MyMatchScorePanel from "@/components/shared/MyMatchScorePanel";

export default function StaffProfile() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const licRef = useRef(null);
  const qualRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => {
    if (user) {
      setF({
        full_name: user.full_name || "",
        phone: user.phone || "",
        county: user.county || "",
        role_categories: user.role_categories || [],
        years_experience: user.years_experience || 0,
        psa_licence_number: user.psa_licence_number || "",
        psa_licence_expiry: user.psa_licence_expiry || "",
        sia_licence_number: user.sia_licence_number || "",
        sia_licence_expiry: user.sia_licence_expiry || "",
        country_of_residence: user.country_of_residence || "IE",
        countries_available: user.countries_available || ["IE"],
        right_to_work_ie: user.right_to_work_ie || false,
        right_to_work_uk: user.right_to_work_uk || false,
        willing_to_travel: user.willing_to_travel || false,
        willing_cross_border: user.willing_cross_border || false,
        cp_qualification: user.cp_qualification || false,
        first_aid_qualification: user.first_aid_qualification || false,
        other_certifications: user.other_certifications || "",
        driving_licence: user.driving_licence || false,
        vehicle_access: user.vehicle_access || false,
        languages: (user.languages || []).join(", "),
        previous_roles: user.previous_roles || "",
        specialist_skills: user.specialist_skills || "",
        emergency_contact: user.emergency_contact || "",
        kit: user.kit || [],
        availability: user.availability || "",
      });
    }
  }, [user]);

  if (!f) return null;

  const toggle = (arr, key, val) => {
    const s = new Set(arr);
    if (s.has(val)) s.delete(val); else s.add(val);
    setF({ ...f, [key]: Array.from(s) });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/staff/profile", {
        ...f,
        years_experience: Number(f.years_experience),
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const upload = async (fileInputRef, doc_type) => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("doc_type", doc_type);
    form.append("file", file);
    try {
      await api.post("/staff/upload-document", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc_type.replace("_", " ")} uploaded`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid={STAFF.profileForm}>
      <h1 className="text-3xl font-outfit font-bold text-white">My Profile</h1>

      {/* Live match score coaching */}
      <MyMatchScorePanel />

      {/* Uploads */}
      <div className="card-flat p-6 space-y-4">
        <h2 className="font-outfit font-semibold text-white">Documents</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-brand" /><span className="font-manrope font-medium">PSA Licence</span></div>
            <input ref={licRef} type="file" accept="image/*,application/pdf" className="text-sm" />
            <Button onClick={() => upload(licRef, "psa_licence")} className="w-full mt-2 bg-brand hover:bg-brand-hover text-white" data-testid={STAFF.uploadLicence}>
              <UploadCloud className="w-4 h-4 mr-2" /> Upload
            </Button>
          </div>
          <div className="border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-brand" /><span className="font-manrope font-medium">Qualification</span></div>
            <input ref={qualRef} type="file" accept="image/*,application/pdf" className="text-sm" />
            <Button onClick={() => upload(qualRef, "qualification")} className="w-full mt-2 bg-brand hover:bg-brand-hover text-white" data-testid={STAFF.uploadQualification}>
              <UploadCloud className="w-4 h-4 mr-2" /> Upload
            </Button>
          </div>
          <div className="border border-white/10 rounded-md p-4">
            <div className="flex items-center gap-2 mb-2"><ImageIcon className="w-4 h-4 text-brand" /><span className="font-manrope font-medium">Profile Photo</span></div>
            <input ref={photoRef} type="file" accept="image/*" className="text-sm" />
            <Button onClick={() => upload(photoRef, "profile_photo")} className="w-full mt-2 bg-brand hover:bg-brand-hover text-white" data-testid={STAFF.uploadPhoto}>
              <UploadCloud className="w-4 h-4 mr-2" /> Upload
            </Button>
          </div>
        </div>
        {user?.documents?.length > 0 && (
          <div className="text-sm text-white/60 font-manrope">
            {user.documents.length} document(s) uploaded — only admin can view your private files.
          </div>
        )}
      </div>

      {/* English verification */}
      <EnglishVerificationCard user={user} refresh={refresh} />

      <form onSubmit={save} className="card-flat p-6 space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="prof-name" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="prof-phone" /></div>
          <div><Label>Country of residence</Label>
            <select value={f.country_of_residence} onChange={(e) => setF({ ...f, country_of_residence: e.target.value })} className="w-full h-10 rounded-md border border-white/10 bg-[#0A0A0A] px-3 text-sm" data-testid="prof-country">
              <option value="IE">Ireland</option>
              <option value="UK">United Kingdom</option>
            </select>
          </div>
          <div><Label>{f.country_of_residence === "UK" ? "UK region" : "Irish county"}</Label><Input value={f.county} onChange={(e) => setF({ ...f, county: e.target.value })} data-testid="prof-county" /></div>
          <div><Label>Years of experience</Label><Input type="number" min={0} value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} data-testid="prof-exp" /></div>
          <div><Label>PSA Licence #</Label><Input value={f.psa_licence_number} onChange={(e) => setF({ ...f, psa_licence_number: e.target.value })} data-testid="prof-psa" /></div>
          <div><Label>PSA Expiry</Label><Input type="date" value={f.psa_licence_expiry} onChange={(e) => setF({ ...f, psa_licence_expiry: e.target.value })} /></div>
          <div><Label>SIA Licence # (UK)</Label><Input value={f.sia_licence_number} onChange={(e) => setF({ ...f, sia_licence_number: e.target.value })} data-testid="prof-sia" /></div>
          <div><Label>SIA Expiry</Label><Input type="date" value={f.sia_licence_expiry} onChange={(e) => setF({ ...f, sia_licence_expiry: e.target.value })} /></div>
          <div><Label>Languages (comma-separated)</Label><Input value={f.languages} onChange={(e) => setF({ ...f, languages: e.target.value })} /></div>
          <div><Label>Emergency contact</Label><Input value={f.emergency_contact} onChange={(e) => setF({ ...f, emergency_contact: e.target.value })} /></div>
        </div>

        {/* Country availability + Right to work */}
        <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Where I can work</div>
          <div className="grid md:grid-cols-2 gap-2 text-sm font-manrope">
            <label className="flex items-center gap-2"><Checkbox checked={(f.countries_available || []).includes("IE")} onCheckedChange={(v) => { const s = new Set(f.countries_available); if (v) s.add("IE"); else s.delete("IE"); setF({ ...f, countries_available: Array.from(s) }); }} data-testid="prof-avail-ie" /> Available in Ireland</label>
            <label className="flex items-center gap-2"><Checkbox checked={(f.countries_available || []).includes("UK")} onCheckedChange={(v) => { const s = new Set(f.countries_available); if (v) s.add("UK"); else s.delete("UK"); setF({ ...f, countries_available: Array.from(s) }); }} data-testid="prof-avail-uk" /> Available in UK</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.right_to_work_ie} onCheckedChange={(v) => setF({ ...f, right_to_work_ie: !!v })} data-testid="prof-rtw-ie" /> Right to work in Ireland</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.right_to_work_uk} onCheckedChange={(v) => setF({ ...f, right_to_work_uk: !!v })} data-testid="prof-rtw-uk" /> Right to work in UK</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.willing_to_travel} onCheckedChange={(v) => setF({ ...f, willing_to_travel: !!v })} data-testid="prof-travel" /> Willing to travel within country</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.willing_cross_border} onCheckedChange={(v) => setF({ ...f, willing_cross_border: !!v })} data-testid="prof-cross-border" /> Willing to work cross-border</label>
          </div>
        </div>

        {/* Additional qualifications */}
        <div>
          <Label className="mb-2 block">Additional qualifications</Label>
          <div className="flex flex-wrap gap-4 text-sm font-manrope">
            <label className="flex items-center gap-2"><Checkbox checked={f.cp_qualification} onCheckedChange={(v) => setF({ ...f, cp_qualification: !!v })} data-testid="prof-cp" /> Close Protection qualified</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.first_aid_qualification} onCheckedChange={(v) => setF({ ...f, first_aid_qualification: !!v })} data-testid="prof-firstaid" /> First Aid certified</label>
          </div>
          <div className="mt-2"><Label>Other security certifications</Label><Textarea rows={2} value={f.other_certifications} onChange={(e) => setF({ ...f, other_certifications: e.target.value })} data-testid="prof-other-certs" /></div>
        </div>

        <div>
          <Label className="mb-2 block">Role categories</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {ROLE_CATEGORIES.map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm font-manrope cursor-pointer">
                <Checkbox checked={f.role_categories.includes(r)} onCheckedChange={() => toggle(f.role_categories, "role_categories", r)} />
                <span>{r}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Kit</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {KIT_ITEMS.map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm font-manrope cursor-pointer">
                <Checkbox checked={f.kit.includes(k)} onCheckedChange={() => toggle(f.kit, "kit", k)} />
                <span>{k}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm font-manrope">
            <Checkbox checked={f.driving_licence} onCheckedChange={(v) => setF({ ...f, driving_licence: !!v })} /> Driving licence
          </label>
          <label className="flex items-center gap-2 text-sm font-manrope">
            <Checkbox checked={f.vehicle_access} onCheckedChange={(v) => setF({ ...f, vehicle_access: !!v })} /> Vehicle access
          </label>
        </div>

        <div><Label>Availability</Label><Textarea rows={2} value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} /></div>
        <div><Label>Previous roles</Label><Textarea rows={3} value={f.previous_roles} onChange={(e) => setF({ ...f, previous_roles: e.target.value })} /></div>
        <div><Label>Specialist skills</Label><Textarea rows={3} value={f.specialist_skills} onChange={(e) => setF({ ...f, specialist_skills: e.target.value })} /></div>

        <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-hover text-white px-6 py-6 font-manrope font-semibold" data-testid="prof-save-btn">
          {busy ? "Saving…" : "Save Profile"}
        </Button>
      </form>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * EnglishVerificationCard — upload certificate OR request assessment
 * ------------------------------------------------------------------------- */
function EnglishVerificationCard({ user, refresh }) {
  const [mode, setMode] = useState("certificate");   // "certificate" | "assessment"
  const [certificateType, setCertificateType] = useState("");
  const [score, setScore] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const verified = user?.english_verified;
  const hasCert = !!user?.english_certificate_doc_id;
  const requested = user?.english_assessment_requested;

  const uploadCert = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a certificate file first (PDF or image).");
    if (!certificateType) return toast.error("Select the certificate type.");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("certificate_type", certificateType);
      form.append("score", score);
      await api.post("/staff/english/certificate", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificate submitted for admin review.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const requestAssessment = async () => {
    setBusy(true);
    try {
      await api.post("/staff/english/request-assessment");
      toast.success("Assessment requested. SecuSource admin will contact you shortly.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-6 space-y-4" data-testid="english-verification-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Languages className="w-5 h-5 text-brand" />
            <h2 className="font-outfit font-semibold text-white">English Verification</h2>
            {verified && (
              <span className="verified-chip" data-testid="english-verified-chip">
                <ShieldCheck className="w-3.5 h-3.5" /> Verified
              </span>
            )}
            {!verified && hasCert && (
              <span className="pill pill-pending" data-testid="english-cert-pending">Certificate under review</span>
            )}
            {!verified && !hasCert && requested && (
              <span className="pill pill-pending" data-testid="english-assessment-requested-chip">Assessment requested</span>
            )}
          </div>
          <div className="text-sm text-white/60 font-manrope">
            Declared level: <strong className="text-white">{user?.english_level || "—"}</strong>. SecuSource verifies English proficiency via certificate upload <em>or</em> a short SecuSource-run assessment.
          </div>
        </div>
      </div>

      {verified ? (
        <div className="rounded-lg border border-gold-200 bg-gold-50 p-3 text-sm font-manrope text-gold-700" data-testid="english-verified-note">
          <ShieldCheck className="w-4 h-4 inline mr-1 text-gold-500" />
          Your English proficiency has been verified by SecuSource admin. This adds a public trust badge to your profile.
        </div>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setMode("certificate")}
              className={`px-3 py-1.5 rounded-md text-xs font-outfit font-bold uppercase tracking-widest border ${mode === "certificate" ? "bg-brand text-white border-brand" : "bg-[#0A0A0A] text-white/70 border-white/10 hover:border-brand"}`}
              data-testid="english-mode-certificate"
            >
              Upload certificate
            </button>
            <button
              type="button"
              onClick={() => setMode("assessment")}
              className={`px-3 py-1.5 rounded-md text-xs font-outfit font-bold uppercase tracking-widest border ${mode === "assessment" ? "bg-brand text-white border-brand" : "bg-[#0A0A0A] text-white/70 border-white/10 hover:border-brand"}`}
              data-testid="english-mode-assessment"
            >
              Request SecuSource assessment
            </button>
          </div>

          {mode === "certificate" ? (
            <div className="rounded-lg border border-white/10 bg-[#0E0E0E]/50 p-4 space-y-3">
              <div className="text-sm font-manrope text-white/85">
                Upload an official English proficiency certificate — IELTS, TOEFL, Cambridge, PTE, Duolingo or equivalent.
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Certificate type</Label>
                  <select value={certificateType} onChange={(e) => setCertificateType(e.target.value)} className="w-full h-10 rounded-md border border-white/10 bg-[#0A0A0A] px-3 text-sm font-manrope" data-testid="english-cert-type">
                    <option value="">Select certificate…</option>
                    <option value="IELTS">IELTS</option>
                    <option value="TOEFL">TOEFL</option>
                    <option value="Cambridge">Cambridge (FCE/CAE/CPE)</option>
                    <option value="PTE">PTE Academic</option>
                    <option value="Duolingo">Duolingo English Test</option>
                    <option value="Trinity">Trinity ISE / GESE</option>
                    <option value="LanguageCert">LanguageCert</option>
                    <option value="Other">Other equivalent</option>
                  </select>
                </div>
                <div>
                  <Label>Score / grade (optional)</Label>
                  <Input value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g., IELTS 6.5 · CAE C1 · TOEFL 90" data-testid="english-cert-score" />
                </div>
              </div>
              <div>
                <Label>Certificate file (PDF or image)</Label>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="text-sm block mt-1" data-testid="english-cert-file" />
              </div>
              <Button onClick={uploadCert} disabled={busy} className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="english-cert-submit">
                <UploadCloud className="w-4 h-4 mr-2" /> {busy ? "Uploading…" : "Submit certificate for review"}
              </Button>
              <div className="text-xs text-white/60 font-manrope">
                Private to SecuSource admin — only they see the file. Admin reviews & marks you verified.
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-white/10 bg-[#0E0E0E]/50 p-4 space-y-3">
              <div className="text-sm font-manrope text-white/85">
                No certificate? Request a short SecuSource assessment — a 5–10 minute call with an admin to confirm your English level.
              </div>
              {requested ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-manrope text-amber-900" data-testid="english-assessment-pending">
                  <PhoneCall className="w-4 h-4 inline mr-1 text-amber-700" />
                  Assessment requested. SecuSource will contact you to schedule the call. You can also upload a certificate to skip the wait.
                </div>
              ) : (
                <Button onClick={requestAssessment} disabled={busy} className="bg-brand hover:bg-brand-hover text-white font-manrope font-semibold" data-testid="english-assessment-request">
                  <PhoneCall className="w-4 h-4 mr-2" /> {busy ? "Requesting…" : "Request assessment"}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
